import React, { memo, useEffect, useRef, useState } from 'react';
import { useTranslation } from 'react-i18next';
import * as cts from '../../../../constants/ui/svg';
import { IMAGE_BASE_URL } from '../../../../constants';
import { useAppDispatch, useAppSelector } from 'hooks';
import { rankingAction } from 'slices/rankingSlice';
import { getToken } from 'helpers';
import { Modal, Tooltip } from 'antd';
import ModalDeleteImage from '../ModalDeleteImage';
import Cropper, { Area, Point } from 'react-easy-crop';
import { getCroppedVideo } from 'helpers/functionHelper';
import moment from 'moment';
import { useDimension } from 'hooks/useDimension';

interface IStandByVideosProps {
  listImageWaitingScreen: Array<any>;
  setListImageWaitingScreen: React.Dispatch<React.SetStateAction<any>>;
}
const defaultCroppedArea: Area = {
  height: 0,
  width: 0,
  x: 0,
  y: 0,
};
const StandByVideos: React.FC<IStandByVideosProps> = (props) => {
  const { listImageWaitingScreen, setListImageWaitingScreen } = props;
  const { t } = useTranslation();
  const dispatch = useAppDispatch();
  const token = getToken();

  const videoMimeType = /(video)\/(mp4|mov|avi|mkv|webm)/i;

  const { width } = useDimension();
  const isMobile = width < 768;

  const inputRef = useRef<any>();
  // https://stackoverflow.com/questions/35701326/how-to-stop-ondragleave-from-firing-when-moving-from-container-to-children-in-re
  // Because of firing onMouseLeave from children element to parent element, we will use this drag counter to handle it
  const dragCounter = useRef(0);

  const [crop, setCrop] = useState<Point>({ x: 0, y: 0 });
  const [showVideoDetail, setShowVideoDetail] = useState<boolean>(false);
  const [selectedVideoDetail, setSelectedVideoDetail] = useState<any>();
  const [openDeleteMediaModal, setOpenDeleteMediaModal] = useState<boolean>(false);
  const [isDraggedInDropzone, setIsDraggedInDropzone] = useState<boolean>(false);
  const [openCropModal, setOpenCropModal] = useState<boolean>(false);
  const [mediaFileEdit, setMediaFileEdit] = useState<string>('');
  const [zoomRatio, setZoomRatio] = useState<number>(1);
  const [croppedArea, setCroppedArea] = useState<Area>(defaultCroppedArea);
  const [showCropVideo, setShowCropVideo] = useState<boolean>(false);
  const [videoAspect, setVideoAspect] = useState(1);

  // this state will observe video is cropping or not
  const [isCroppingVideo, setIsCroppingVideo] = useState<boolean>(false);
  // this state will observe the process of cropping video (return string has .toFixed(1))
  const [percentCropped, setPercentCropped] = useState<string>('');

  const [renderCropper, setRenderCropper] = useState(false);

  useEffect(() => {
    if (openCropModal) {
      setTimeout(() => setRenderCropper(true), 300);
    } else {
      setRenderCropper(false);
    }
  }, [openCropModal]);
  const handleVideoLoad = (video: HTMLVideoElement) => {
    const aspect = video.videoWidth / video.videoHeight;
    setVideoAspect(aspect);
  };
  const selectShop = useAppSelector((state) => state.shopList.selectShop);
  const listPermissionByUser = useAppSelector((state) => state.shopSetting.permissionByUser);

  const listShopHasPermission = listPermissionByUser
    .filter((item) => item.name == 'enable_ranking_program_master')
    .map((mapItem) => mapItem.shop_id);

  // common
  const mediaUrlLink = (url: string, typeImage: string) => {
    return `${IMAGE_BASE_URL}/${typeImage}/${url}`;
  };

  // funcs
  const handleToggleShowCropVideo = () => {
    setShowCropVideo(!showCropVideo);
  };
  const onCropComplete = (croppedArea: Area, croppedAreaPixels: Area) => {
    setCroppedArea(croppedAreaPixels);
  };
  const handleToggleShowVideoDetail = () => {
    setShowVideoDetail(!showVideoDetail);
  };

  const autoSave = (listImage: any) => {
    if (token) {
      dispatch(
        rankingAction.addStandByMedia({
          token,
          listMedia: listImage.map((item: any) => {
            return {
              imageFile: item.imageFile,
              order_by: item?.order_by,
              ...(selectShop?.id
                ? { shop_id: selectShop.id }
                : { shop_ids: listShopHasPermission }),
            };
          }),
        })
      );
    }
  };

  const processFiles = async (files: File[], disableAutoSave: boolean = false) => {
    if (!files.length) return;
    const filesArr = [...files];
    const validFiles = filesArr.filter((file) => file.type.match(videoMimeType));

    if (validFiles.length === 0) {
      alert('Video type is not valid');
      return;
    }

    // Convert each file to Promise reader
    const readFilePromises = validFiles.map((file) => {
      return new Promise((resolve) => {
        const reader = new FileReader();
        reader.onload = () => {
          resolve({
            file,
            result: reader.result,
          });
        };
        reader.readAsDataURL(file);
      });
    });

    // Wait for all files to finish reading
    const results: any[] = await Promise.all(readFilePromises);

    const newVideos = results.map(({ file, result }) => {
      const maxOrder = Math.max(
        ...listImageWaitingScreen.map((item: any) => item.order_by || 0),
        0
      );

      return {
        imageUrl: result,
        imageFile: file,
        isNew: true,
        order_by: maxOrder + 1,
        fileType: 'video',
      };
    });

    setListImageWaitingScreen((prev: any[]) => {
      const finalList = [...prev, ...newVideos];
      if (!disableAutoSave) {
        autoSave(newVideos);
      }
      return finalList;
    });
  };

  const handleCropVideo = async () => {
    const croppedVideoBlob: any = await getCroppedVideo(mediaFileEdit, croppedArea, (percent) => {
      setPercentCropped(percent.toFixed(1));
    });
    if (croppedVideoBlob) {
      setIsCroppingVideo(false);
    }
    const file = new File([croppedVideoBlob], `stand_by_media_${moment.now()}.mp4`, {
      type: 'video/mp4',
    });
    toggleOpenCropperModal();

    // reset after crop
    setCroppedArea(defaultCroppedArea);
    setZoomRatio(1);
    processFiles([file]);
  };

  const toggleOpenCropperModal = () => {
    if (isCroppingVideo) return;
    setOpenCropModal(!openCropModal);
  };

  const onDragEnter = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current += 1;

    if (dragCounter.current === 1) {
      setIsDraggedInDropzone(true);
    }
  };

  const onDragLeave = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current -= 1;

    if (dragCounter.current === 0) {
      setIsDraggedInDropzone(false);
    }
  };

  const onDrop = async (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    dragCounter.current = 0;
    setIsDraggedInDropzone(false);
    const files = Array.from(e.dataTransfer.files);
    const previewUrl = URL.createObjectURL(files[0]);
    setMediaFileEdit(previewUrl);
    toggleOpenCropperModal();
    // await processFiles(files);
  };

  const handleDeleteImage = () => {
    if (token) {
      if (selectShop.id) {
        dispatch(
          rankingAction.deleteImageSlider({
            token,
            id: selectedVideoDetail?.id,
            data: { shop_id: selectShop?.id },
          })
        );
      } else {
        dispatch(
          rankingAction.deleteImageSlider({
            token,
            id: selectedVideoDetail?.id,
            data: { shop_ids: listShopHasPermission },
          })
        );
      }
      handleToggleShowVideoDetail();
    }
    setListImageWaitingScreen((prevList: any) =>
      prevList.filter((item: any) => item?.id !== selectedVideoDetail?.id)
    );
    setOpenDeleteMediaModal(false);
  };

  // upload images
  const handleUploadStandbyImage = (e: React.ChangeEvent<HTMLInputElement>) => {
    const input = e.target;
    const files = Array.from(input.files || []);
    if (!files.length) return;
    const previewUrl = URL.createObjectURL(files[0]);
    // processFiles(files, true);
    setMediaFileEdit(previewUrl);
    toggleOpenCropperModal();
    input.value = '';
  };

  // render
  const __renderStandByVideo = (item: any, src: string) => {
    return (
      <Tooltip
        title={isMobile ? '' : String(src)}
        placement="top"
        overlayClassName="no-padding-bottom-tooltip"
        trigger={['hover', 'click']}
      >
        <div className="lg:w-[98px] w-full lg:h-auto h-full">
          <div
            onClick={() => {
              handleToggleShowVideoDetail();
              setSelectedVideoDetail(item);
            }}
            className="relative rounded-lg cursor-pointer lg:size-[98px] w-full aspect-square overflow-hidden"
          >
            <video
              draggable={false}
              src={item.imageUrl ? item.imageUrl : mediaUrlLink(src, 'images_slider')}
              className=" lg:size-[98px] size-full rounded-lg object-cover pointer-events-none"
              autoPlay={!isMobile}
              loop={!isMobile}
              muted
            />
          </div>
          <div>
            <span className="w-full text-base break-all cursor-pointer line-clamp-2">{src}</span>
          </div>
        </div>
      </Tooltip>
    );
  };

  const handleCheckArrHasData = (arrData: Array<any>) => {
    if (!arrData.length) return <></>;
    return arrData;
  };

  const handleZoom = (type: 'zoom-in' | 'zoom-out') => {
    if (type == 'zoom-out') {
      if (zoomRatio > 1) {
        setZoomRatio((prev) => (prev -= 0.1));
      }
    } else {
      if (zoomRatio <= 3) {
        setZoomRatio((prev) => (prev += 0.1));
      }
    }
  };

  // fetch standby images
  useEffect(() => {
    if (token) {
      if (selectShop.id) {
        const params = {
          shop_id: selectShop?.id,
          type: 1,
        };
        dispatch(rankingAction.getImageSlider({ token, params }));
      } else {
        const params = {
          permission: 'master',
          type: 1,
        };
        dispatch(rankingAction.getImageSlider({ token, params }));
      }
    }
  }, [selectShop.id, token]);

  return (
    <div
      className="relative px-4 py-3 h-full"
      onDragEnter={onDragEnter}
      onDragLeave={onDragLeave}
      onDrop={onDrop}
      onDragOver={(e) => e.preventDefault()}
    >
      {/* Standby images header */}
      <div className="flex justify-between items-center">
        <span className="text-xl font-notosans-700">{t('MOVIE')}</span>
        <button
          onClick={() => inputRef.current?.click()}
          className="flex gap-2 items-center px-4 h-11 text-base text-white rounded-lg font-notosans-400 bg-primary"
        >
          <span>{cts.add}</span>
          <span>{t('UPLOAD')}</span>
        </button>
      </div>
      <input
        id="inputAvatar"
        ref={inputRef}
        accept=".mp4, .mov, .avi, .mkv, .webm"
        type="file"
        onChange={handleUploadStandbyImage}
        className="hidden"
        // multiple
      />
      {/* Standby images body*/}
      {/* PC */}
      <div className="media-container lg:flex hidden flex-wrap max-h-[calc(100vh-244px)] h-fit gap-4 mt-3 overflow-auto">
        {handleCheckArrHasData(listImageWaitingScreen) &&
          listImageWaitingScreen
            .filter((item: any) => item?.link?.includes('mp4') || item?.fileType === 'video')
            .map((item) => (
              <React.Fragment key={item.id}>{__renderStandByVideo(item, item.link)}</React.Fragment>
            ))}
      </div>

      {/* Mobile */}
      <div className="media-container lg:hidden grid-cols-2 grid max-h-[calc(100vh-244px)] h-fit gap-4 mt-3 overflow-auto">
        {handleCheckArrHasData(listImageWaitingScreen) &&
          listImageWaitingScreen
            .filter((item: any) => item?.link?.includes('mp4') || item?.fileType === 'video')
            .map((item) => (
              <React.Fragment key={item.id}>{__renderStandByVideo(item, item.link)}</React.Fragment>
            ))}
      </div>
      {/* Modal select video detail */}
      <Modal
        open={showVideoDetail}
        onOk={handleToggleShowVideoDetail}
        onCancel={handleToggleShowVideoDetail}
        destroyOnClose
        title={null}
        footer={null}
        closeIcon={<></>}
        width={'fit-content'}
        bodyStyle={{
          padding: 0,
          borderRadius: 8,
        }}
        className="override-antd-modal-border-radius"
        centered
      >
        {/* Modal header */}
        <div className="flex justify-between items-center px-4 py-2 bg-white rounded-t-lg border-b border-border-gray">
          <p className="text-xl font-notosans-700 text-gray_800">{t('MOVIE')}</p>
          <button className="text-base text-primary font-notosans-400">{t('CLOSE')}</button>
        </div>

        {/* Modal body */}
        <div className="size-[450px] border-none bg-white !rounded-b-lg">
          {
            <video
              src={
                selectedVideoDetail?.link
                  ? mediaUrlLink(selectedVideoDetail?.link, 'images_slider')
                  : selectedVideoDetail?.imageUrl
              }
              className="object-contain w-full h-full"
              autoPlay
              loop
              muted
              controls={false}
            />
          }
        </div>
        <div className="p-3 w-full text-sm text-center truncate font-notosans-300">
          {selectedVideoDetail?.link}
        </div>
        {/* Modal footer */}
        <div className="flex gap-2 items-center p-2 bg-white rounded-b-lg border-t border-border-gray">
          <button
            onClick={handleToggleShowVideoDetail}
            className="w-1/2 h-9 text-base rounded-lg border border-solid border-border-gray text-primary font-notosans-400"
          >
            {t('CANCEL')}
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setOpenDeleteMediaModal(true);
            }}
            className="w-1/2 text-base h-9 border border-solid border-[#F04438] font-notosans-400 text-[#F04438] flex items-center gap-2 rounded-lg justify-center"
          >
            {cts.trashSimpleIcon}
            {t('DELETE')}
          </button>
        </div>
      </Modal>
      <ModalDeleteImage
        openConfirm={openDeleteMediaModal}
        setOpenConfirm={setOpenDeleteMediaModal}
        handleDeleteImage={handleDeleteImage}
        title={t('CONFIRM_DEL_IMG')}
        isInProgram={Boolean(selectedVideoDetail?.order_by_images_slider_ranking[0]?.id)}
      />
      {isDraggedInDropzone && (
        <div className="flex absolute inset-0 z-50 justify-center items-center pointer-events-none bg-black/30">
          <div className="text-2xl font-semibold text-white">{t('PLEASE_UPLOAD_THE_VIDEO')}</div>
        </div>
      )}
      <Modal
        centered
        open={openCropModal}
        onOk={toggleOpenCropperModal}
        onCancel={toggleOpenCropperModal}
        width={600}
        bodyStyle={{
          padding: 0,
        }}
        destroyOnClose
        title={null}
        footer={null}
        closeIcon={<></>}
        className="override-antd-modal-border-radius"
      >
        <div className="flex justify-between items-center px-4 py-2 bg-white rounded-t-lg border-b border-border-gray">
          <p className="text-xl font-notosans-700 text-gray_800">{t('MOVIE')}</p>
          <button className="text-base text-primary font-notosans-400">{t('CLOSE')}</button>
        </div>
        <div className="flex relative flex-col">
          <div className="flex flex-col">
            <div className="relative w-full h-[380px]">
              {/* https://github.com/ValentinH/react-easy-crop/discussions/398 */}
              {renderCropper ? (
                <Cropper
                  video={mediaFileEdit}
                  crop={crop}
                  onCropChange={setCrop}
                  zoom={zoomRatio}
                  onZoomChange={setZoomRatio}
                  onCropComplete={onCropComplete}
                  aspect={1}
                  cropShape="rect"
                  showGrid={false}
                  objectFit="contain"
                  // cropSize={{ width: 370, height: 370 }}
                  // onMediaLoaded={(mediaSize) => {
                  //   setVideoAspect(mediaSize.width / mediaSize.height);
                  // }}

                  minZoom={1}
                  style={{
                    containerStyle: {
                      width: '100%',
                      height: '100%',
                    },
                  }}
                />
              ) : (
                <></>
              )}
            </div>
          </div>

          <div className="flex gap-2 items-center mx-auto w-fit">
            <button onClick={() => handleZoom('zoom-out')}>{cts.zoomOut}</button>
            <input
              type="range"
              min={1}
              max={3}
              step={0.1}
              value={zoomRatio}
              className="py-3 w-[200px] accent-primary mx-auto"
              onChange={(e) => setZoomRatio(Number(e.target.value))}
            />
            <button onClick={() => handleZoom('zoom-in')}>{cts.zoomIn}</button>
          </div>
        </div>
        {/* Modal footer */}
        <div className="flex gap-2 items-center p-2 bg-white rounded-b-lg border-t border-border-gray">
          <button
            onClick={toggleOpenCropperModal}
            className="w-1/2 h-9 text-base rounded-lg border border-solid border-border-gray text-primary font-notosans-400"
          >
            {t('CANCEL')}
          </button>
          <button
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setIsCroppingVideo(true);
              handleCropVideo();
            }}
            className="w-1/2 h-9 text-base text-white rounded-lg bg-primary font-notosans-400"
          >
            {!isCroppingVideo ? t('KEEP') : `保存（${percentCropped}％)`}
          </button>
        </div>
      </Modal>
    </div>
  );
};

export default memo(StandByVideos);
